import {
    TagIcon,
    ShieldCheckIcon,
    PhoneIcon,
    CalendarDaysIcon,
} from "@heroicons/react/24/outline";

export default function BookingBenefits() {
    const benefits = [
        {
            icon: TagIcon,
            number: "01",
            title: "Best Price Guarantee",
            description:
                "Get competitive Dubai hotel rates and exclusive local deals designed to give you more value.",
        },
        {
            icon: ShieldCheckIcon,
            number: "02",
            title: "Secure Booking",
            description:
                "Book with confidence. Your booking details and personal information are kept secure.",
        },
        {
            icon: PhoneIcon,
            number: "03",
            title: "24/7 Local Support",
            description:
                "Need help with your Dubai stay? Our local support team is here whenever you need us.",
        },
        {
            icon: CalendarDaysIcon,
            number: "04",
            title: "Flexible Stays",
            description:
                "Find hotel options with flexible policies that make planning your Dubai trip easier.",
        },
    ];

    return (
        <section
            className="
                relative
                w-full
                overflow-hidden

                bg-white
                 max-w-[1536px] mx-auto
              px-4
            sm:px-6
            lg:px-8

                py-16

                sm:py-20

                lg:py-24
            "
        >
            {/* =====================================================
                SOFT BACKGROUND DECORATION
            ===================================================== */}

            <div
                className="
                    pointer-events-none
                    absolute
                    -left-40
                    top-10

                    h-80
                    w-80

                    rounded-full

                    bg-[#0B6E8E]/[0.035]

                    blur-3xl
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    -right-40
                    bottom-0

                    h-80
                    w-80

                    rounded-full

                    bg-[#F7B928]/[0.045]

                    blur-3xl
                "
            />

            <div
                className="
                    relative
                    mx-auto
                    w-full
                    max-w-[1536px]
                "
            >
                {/* =====================================================
                    SECTION INTRO
                ===================================================== */}

                <div
                    className="
                        mx-auto
                        max-w-[1536px]

                        text-center

                        mb-10

                        sm:mb-12

                        lg:mb-14
                    "
                >
                    {/* EYEBROW */}

                    <div
                        className="
                            mb-3

                            flex
                            items-center
                            justify-center
                            gap-3
                        "
                    >
                        <span
                            className="
                                h-[2px]
                                w-7

                                rounded-full

                                bg-[#F7B928]
                            "
                        />

                        <span
                            className="
                                text-[9px]
                                font-bold
                                uppercase
                                tracking-[0.20em]

                                text-[#0B6E8E]

                                sm:text-[10px]
                            "
                        >
                            Why Book With Us
                        </span>

                        <span
                            className="
                                h-[2px]
                                w-7

                                rounded-full

                                bg-[#F7B928]
                            "
                        />
                    </div>

                    {/* HEADING */}

                    <h2
                        className="
                            text-[29px]
                            font-extrabold

                            leading-[1.08]

                            tracking-[-0.035em]

                            text-[#062F4F]

                            sm:text-[34px]

                            md:text-[40px]

                            lg:text-[44px]
                        "
                    >
                        Everything you need for a
                        <span className="text-[#0B6E8E]">
                            {" "}better Dubai stay.
                        </span>
                    </h2>

                    {/* DESCRIPTION */}

                    <p
                        className="
                            mx-auto
                            mt-3

                            max-w-[650px]

                            text-[12px]
                            leading-5

                            text-[#71808A]

                            sm:text-[13px]
                            sm:leading-6
                        "
                    >
                        We make finding and booking your Dubai hotel
                        simple, transparent and stress-free — from
                        competitive local rates to support throughout
                        your stay.
                    </p>
                </div>

                {/* =====================================================
                    BENEFIT CARDS
                ===================================================== */}

                <div
                    className="
                        grid

                        grid-cols-1
                        gap-4

                        sm:grid-cols-2

                        lg:grid-cols-4
                        lg:gap-5
                    "
                >
                    {benefits.map((benefit) => {
                        const Icon = benefit.icon;

                        return (
                            <div
                                key={benefit.title}
                                className="
                                    group

                                    relative
                                    overflow-hidden

                                    rounded-[18px]

                                    border
                                    border-[#E3E9ED]

                                    bg-white

                                    p-5

                                    shadow-[0_8px_30px_rgba(6,47,79,0.055)]

                                    transition-all
                                    duration-300

                                    hover:-translate-y-1

                                    hover:border-[#0B6E8E]/20

                                    hover:shadow-[0_16px_40px_rgba(6,47,79,0.10)]

                                    sm:p-6
                                "
                            >
                                {/* TOP NUMBER */}

                                <div
                                    className="
                                        absolute
                                        right-5
                                        top-5

                                        text-[9px]
                                        font-extrabold
                                        tracking-[0.14em]

                                        text-[#F7B928]
                                    "
                                >
                                    {benefit.number}
                                </div>

                                {/* ICON */}

                                <div
                                    className="
                                        mb-5

                                        flex
                                        h-12
                                        w-12

                                        items-center
                                        justify-center

                                        rounded-[14px]

                                        bg-[#EEF7FA]

                                        transition-all
                                        duration-300

                                        group-hover:bg-[#0B6E8E]
                                    "
                                >
                                    <Icon
                                        className="
                                            h-6
                                            w-6

                                            text-[#0B6E8E]

                                            transition-colors
                                            duration-300

                                            group-hover:text-white
                                        "
                                        strokeWidth={1.8}
                                    />
                                </div>

                                {/* TITLE */}

                                <h3
                                    className="
                                        pr-8

                                        text-[16px]
                                        font-extrabold

                                        leading-tight

                                        text-[#062F4F]

                                        transition-colors
                                        duration-300

                                        group-hover:text-[#0B6E8E]

                                        sm:text-[17px]
                                    "
                                >
                                    {benefit.title}
                                </h3>

                                {/* DESCRIPTION */}

                                <p
                                    className="
                                        mt-2.5

                                        text-[11px]
                                        leading-5

                                        text-[#71808A]

                                        sm:text-[12px]
                                        sm:leading-5
                                    "
                                >
                                    {benefit.description}
                                </p>

                                {/* BOTTOM ACCENT */}

                                <div
                                    className="
                                        mt-5

                                        h-[2px]
                                        w-8

                                        rounded-full

                                        bg-[#F7B928]

                                        transition-all
                                        duration-300

                                        group-hover:w-14
                                    "
                                />

                                {/* DECORATIVE CIRCLE */}

                                <div
                                    className="
                                        pointer-events-none

                                        absolute
                                        -bottom-14
                                        -right-14

                                        h-28
                                        w-28

                                        rounded-full

                                        border
                                        border-[#0B6E8E]/[0.06]

                                        transition-transform
                                        duration-500

                                        group-hover:scale-125
                                    "
                                />
                            </div>
                        );
                    })}
                </div>

                {/* =====================================================
                    TRUST STRIP
                ===================================================== */}

                <div
                    className="
                        mt-8

                        flex
                        flex-col
                        items-center
                        justify-center

                        gap-3

                        rounded-[14px]

                        border
                        border-[#E7EDF1]

                        bg-[#F8FBFC]

                        px-5
                        py-4

                        sm:flex-row
                        sm:gap-5
                    "
                >
                    {/* CHECK */}

                    <div
                        className="
                            flex
                            h-8
                            w-8
                            shrink-0

                            items-center
                            justify-center

                            rounded-full

                            bg-[#0B6E8E]

                            text-[13px]
                            font-bold

                            text-white
                        "
                    >
                        ✓
                    </div>

                    {/* TEXT */}

                    <div className="text-center sm:text-left">
                        <p
                            className="
                                text-[11px]
                                font-extrabold

                                text-[#062F4F]

                                sm:text-[12px]
                            "
                        >
                            Your Dubai stay starts with the right hotel.
                        </p>

                        <p
                            className="
                                mt-0.5

                                text-[9px]
                                text-[#71808A]

                                sm:text-[10px]
                            "
                        >
                            Local deals • Verified hotels • Secure booking •
                            Dedicated support
                        </p>
                    </div>

                    {/* GOLD STARS */}

                    <div
                        className="
                            flex
                            items-center
                            gap-0.5

                            sm:ml-auto
                        "
                    >
                        <span className="text-[#F7B928]">★</span>
                        <span className="text-[#F7B928]">★</span>
                        <span className="text-[#F7B928]">★</span>
                        <span className="text-[#F7B928]">★</span>
                        <span className="text-[#F7B928]">★</span>
                    </div>
                </div>
            </div>
        </section>
    );
}